import React, { useState, useEffect, useRef } from 'react';
import { FaApple, FaMicrosoft, FaAmazon } from 'react-icons/fa';
import { IoTrendingUp, IoTrendingDown } from 'react-icons/io5';
import { SiTesla } from 'react-icons/si';

const LiveTransactionsBot = () => {
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [volumeToday, setVolumeToday] = useState(0);
  const transactionQueueRef = useRef([]);
  const animationTimeoutRef = useRef(null);

  // Generate random phone number
  const generateRandomMobileNumber = () => {
    const prefixes = ['01', '07', '2547'];
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const middleDigits = Math.floor(Math.random() * 9000) + 1000;
    const lastDigits = Math.floor(Math.random() * 9000) + 1000;
    return `${prefix}${middleDigits}${lastDigits}`;
  };

  // Generate random amount
  const generateRandomAmount = () => {
    return Math.floor(Math.random() * (50000 - 500 + 1)) + 500;
  };

  // Generate random stock
  const getRandomStock = () => {
    const stocks = ['AAPL', 'TSLA', 'MSFT', 'AMZN'];
    return stocks[Math.floor(Math.random() * stocks.length)];
  };

  // Map stock symbol to full name
  const getStockFullName = (symbol) => {
    const map = {
      'AAPL': 'Apple Inc.',
      'TSLA': 'Tesla Inc.',
      'MSFT': 'Microsoft Corp.',
      'AMZN': 'Amazon.com Inc.'
    };
    return map[symbol] || symbol;
  };

  // Get stock icon based on symbol
  const getStockIcon = (stock) => {
    switch(stock) {
      case 'AAPL':
        return <FaApple />;
      case 'TSLA':
        return < SiTesla />;
      case 'MSFT':
        return <FaMicrosoft />;
      case 'AMZN':
        return <FaAmazon />;
      default:
        return <FaApple />;
    }
  };

  // Generate a new transaction
  const generateNewTransaction = () => {
    const type = Math.random() > 0.5 ? 'deposit' : 'withdrwal';
    const stock = getRandomStock();
    
    return {
      id: Date.now() + Math.random(),
      type: type,
      stock: stock,
      stockName: getStockFullName(stock),
      phone: generateRandomMobileNumber(),
      amount: generateRandomAmount(),
      timestamp: 'Just now'
    };
  };

  // Process the next transaction in queue
  const processNextTransaction = () => {
    if (transactionQueueRef.current.length === 0) {
      for (let i = 0; i < 3; i++) {
        transactionQueueRef.current.push(generateNewTransaction());
      }
    }
    
    if (currentTransaction) {
      setIsTransitioning(true);
      
      setTimeout(() => {
        const nextTransaction = transactionQueueRef.current.shift();
        setCurrentTransaction(nextTransaction);
        setVolumeToday(prev => prev + nextTransaction.amount);
        setIsTransitioning(false);
        
        animationTimeoutRef.current = setTimeout(processNextTransaction, 3000);
      }, 400);
    } else {
      const nextTransaction = transactionQueueRef.current.shift();
      setCurrentTransaction(nextTransaction);
      setVolumeToday(prev => prev + nextTransaction.amount);
      
      animationTimeoutRef.current = setTimeout(processNextTransaction, 3000);
    }
  };

  useEffect(() => {
    for (let i = 0; i < 5; i++) {
      transactionQueueRef.current.push(generateNewTransaction());
    }
    
    setVolumeToday(Math.floor(Math.random() * 2000000) + 500000);
    processNextTransaction();
    
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="ge-live-trades">
      <div className="ge-trades-header">
        <h3>Transactions</h3>
        <div className="ge-live-indicator">
          <div className="ge-pulse"></div>
          <span>LIVE</span>
        </div>
      </div>
      
      <div className="ge-trade-container">
        {currentTransaction && (
          <div className={`ge-trade-card ${isTransitioning ? 'exiting' : 'active'}`}>
            <div className="ge-stock-icon">
              <div className={`ge-icon-wrapper ${currentTransaction.stock.toLowerCase()}`}>
                {getStockIcon(currentTransaction.stock)}
              </div>
            </div>
            
            <div className="ge-trade-info">
              <div className="ge-trade-details">
                <span className="ge-stock-symbol">{currentTransaction.stock}</span>
                <div className={`ge-trade-amount ${currentTransaction.type}`}>
                  {currentTransaction.type === 'deposit' ? <IoTrendingUp /> : <IoTrendingDown />}
                  <span>KES {currentTransaction.amount.toLocaleString()}</span>
                </div>
              </div>
              <div className="ge-trade-footer">
                <span className="ge-user-id">
                  {currentTransaction.phone.slice(0, 2)}****{currentTransaction.phone.slice(-3)}
                </span>
                <span className={`ge-trade-type ${currentTransaction.type}`}>
                  {currentTransaction.type}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        .ge-live-trades {
          margin: 12px;
          background: rgba(23, 42, 69, 0.9);
          border-radius: 12px;
          border: 1px solid rgba(231, 76, 60, 0.2);
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .ge-trades-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid rgba(231, 76, 60, 0.1);
          background: linear-gradient(135deg, rgba(231, 76, 60, 0.1), transparent);
        }
        
        .ge-trades-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: white;
        }
        
        .ge-live-indicator {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(46, 204, 113, 0.1);
          border: 1px solid rgba(46, 204, 113, 0.2);
          border-radius: 12px;
          padding: 3px 8px;
          font-size: 10px;
          font-weight: 600;
          color: #2ecc71;
        }
        
        .ge-pulse {
          width: 6px;
          height: 6px;
          background: #2ecc71;
          border-radius: 50%;
          animation: gePulse 1.5s infinite;
        }
        
        .ge-trade-container {
          padding: 12px;
          min-height: 60px;
        }
        
        .ge-trade-card {
          display: flex;
          gap: 12px;
          background: rgba(16, 20, 31, 0.8);
          border: 1px solid rgba(231, 76, 60, 0.1);
          border-radius: 8px;
          padding: 12px;
          opacity: 0;
          transform: translateY(10px);
        }
        
        .ge-trade-card.active {
          animation: geSlideIn 0.4s ease forwards;
        }
        
        .ge-trade-card.exiting {
          animation: geSlideOut 0.4s ease forwards;
        }
        
        .ge-stock-icon {
          flex-shrink: 0;
        }
        
        .ge-icon-wrapper {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: white;
        }
        
        .ge-icon-wrapper.aapl {
          background: linear-gradient(135deg, #007aff, #005ce6);
        }
        
        .ge-icon-wrapper.tsla {
          background: linear-gradient(135deg, #e82127, #cc1e28);
        }
        
        .ge-icon-wrapper.msft {
          background: linear-gradient(135deg, #00a1f1, #0078d4);
        }
        
        .ge-icon-wrapper.amzn {
          background: linear-gradient(135deg, #ff9900, #e68900);
        }
        
        .ge-trade-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 8px;
        }
        
        .ge-trade-details {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .ge-stock-symbol {
          font-size: 14px;
          font-weight: 700;
          color: white;
        }
        
        .ge-trade-amount {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 14px;
          font-weight: 600;
        }
        
        .ge-trade-amount.deposit {
          color: #2ecc71;
        }
        
        .ge-trade-amount.withdrwal {
          color: #e74c3c;
        }
        
        .ge-trade-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .ge-user-id {
          font-size: 11px;
          color: #8892b0;
          font-family: monospace;
        }
        
        .ge-trade-type {
          font-size: 9px;
          font-weight: 600;
          text-transform: uppercase;
          padding: 2px 6px;
          border-radius: 4px;
          letter-spacing: 0.3px;
        }
        
        .ge-trade-type.deposit {
          background: rgba(46, 204, 113, 0.1);
          color: #2ecc71;
          border: 1px solid rgba(46, 204, 113, 0.2);
        }
        
        .ge-trade-type.withdrwal {
          background: rgba(231, 76, 60, 0.1);
          color: #e74c3c;
          border: 1px solid rgba(231, 76, 60, 0.2);
        }
        
        @keyframes gePulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.3;
            transform: scale(1.5);
          }
        }
        
        @keyframes geSlideIn {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes geSlideOut {
          to {
            opacity: 0;
            transform: translateY(-10px);
          }
        }
        
        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .ge-live-trades {
            margin: 8px;
          }
          
          .ge-trades-header {
            padding: 10px 12px;
          }
          
          .ge-trades-header h3 {
            font-size: 13px;
          }
          
          .ge-trade-container {
            padding: 10px;
          }
          
          .ge-trade-card {
            padding: 10px;
            gap: 10px;
          }
          
          .ge-icon-wrapper {
            width: 32px;
            height: 32px;
            font-size: 14px;
          }
          
          .ge-stock-symbol {
            font-size: 13px;
          }
          
          .ge-trade-amount {
            font-size: 12px;
          }
        }
        
        @media (max-width: 480px) {
          .ge-trades-header {
            padding: 8px 10px;
          }
          
          .ge-trade-container {
            padding: 8px;
          }
          
          .ge-trade-card {
            padding: 8px;
          }
          
          .ge-icon-wrapper {
            width: 28px;
            height: 28px;
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default LiveTransactionsBot;